import type { UseQueryResult, DefaultError } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../clients';
import { queryKeys } from '../lib/query-keys';
import { isEmbeddedInSucceed } from '../utils/cp-context';
import type { School } from '../types';

/**
 * Fetches district schools for the filter dropdown.
 *
 *   Embedded (QA/prod) → GET /highschools via succeed-api (cookie auth)
 *   Local dev           → mock-data/schools.json (no backend dependency)
 */
const SUCCEED_SCHOOLS_PATH = '/highschools';

export function useGetSchools(enabled = true): UseQueryResult<School[], DefaultError> {
  const apiClient = useApiClient();

  return useQuery<School[], DefaultError>({
    queryKey: queryKeys.schools.list(),
    queryFn: async (): Promise<School[]> => {
      if (!isEmbeddedInSucceed()) {
        const mockData = await import('../mock-data/schools.json');
        return (mockData.default ?? mockData) as School[];
      }
      const response = await apiClient.get<School[]>(SUCCEED_SCHOOLS_PATH);
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
  });
}
